import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans as TransBase } from 'react-i18next';
import AppShellLayout from '../components/AppShellLayout';
import { useAuth } from '../auth';
import { useBilling } from '../billing';

// react-i18next v17 Trans types use TS 5 generics incompatible with TS 4.9
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Trans = TransBase as any;

interface GmailStatus {
  connected: boolean;
  emailCount: number;
  isIngesting: boolean;
}

interface SweepStatus {
  active: boolean;
  status?: string;
  totalSwept?: number;
  actionId?: number;
}

interface SenderKnowledgeEntry {
  senderEmail: string;
  category: string;
  confidence: number | null;
  classifiedAt: string;
}

interface UserPreferences {
  markAsReadOnArchive: boolean;
  markAsReadOnTrash: boolean;
  toRespondRetentionDays: number;
  toRespondMaxAgeDays: number | null;
  digestEmails: boolean;
  summaryEmails: boolean;
  autoDraftReplies: boolean;
}

interface ReclassificationOffer {
  id: number;
  senderEmail: string;
  oldCategory: string;
  newCategory: string;
  emailCount: number;
}

interface RetentionEntry {
  systemDefault: number | null;
  userOverride: number | null;
  hasOverride: boolean;
  effective: number | null;
}

type RetentionSettings = Record<string, RetentionEntry>;

const RETENTION_CATEGORY_KEYS: Record<string, string> = {
  notification:  'subscriptions.categoryTabs.notification',
  transactional: 'subscriptions.categoryTabs.transactional',
  personal:      'subscriptions.categoryTabs.personal',
  unknown:       'subscriptions.categoryTabs.unknown',
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { billing, loading: billingLoading } = useBilling();
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [gmailLoading, setGmailLoading] = useState(true);
  const [msStatus, setMsStatus] = useState<GmailStatus | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [prefs, setPrefs] = useState<UserPreferences>({ markAsReadOnArchive: true, markAsReadOnTrash: true, toRespondRetentionDays: 7, toRespondMaxAgeDays: null, digestEmails: true, summaryEmails: true, autoDraftReplies: false });
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [sweepStatus, setSweepStatus] = useState<SweepStatus | null>(null);
  const [sweepLoading, setSweepLoading] = useState(true);
  const [sweepStarting, setSweepStarting] = useState(false);
  const [sweepConfirm, setSweepConfirm] = useState(false);
  const [sweepError, setSweepError] = useState<string | null>(null);
  const [knowledge, setKnowledge] = useState<SenderKnowledgeEntry[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(true);
  const [retention, setRetention] = useState<RetentionSettings | null>(null);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionEditing, setRetentionEditing] = useState<string | null>(null);
  const [retentionEditValue, setRetentionEditValue] = useState('');
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [offers, setOffers] = useState<ReclassificationOffer[]>([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offerActioning, setOfferActioning] = useState<number | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [replyHintsCount, setReplyHintsCount] = useState<number | null>(null);
  const [replyHintsClearLoading, setReplyHintsClearLoading] = useState(false);
  const [otpRetention, setOtpRetention] = useState<{ systemDefault: number; userOverride: number | null; hasOverride: boolean; effective: number } | null>(null);
  const [otpRetentionEditing, setOtpRetentionEditing] = useState(false);
  const [otpRetentionEditValue, setOtpRetentionEditValue] = useState('');
  const [otpRetentionSaving, setOtpRetentionSaving] = useState(false);

  useEffect(() => {
    fetch('/api/auth/gmail/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setGmailStatus(data); })
      .catch(() => {})
      .finally(() => setGmailLoading(false));

    fetch('/api/auth/microsoft/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMsStatus(data); })
      .catch(() => {});

    fetch('/api/preferences')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPrefs(data); })
      .catch(() => {})
      .finally(() => setPrefsLoading(false));

    fetch('/api/inbox/sweep/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSweepStatus(data); })
      .catch(() => {})
      .finally(() => setSweepLoading(false));

    fetch('/api/senders/knowledge')
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setKnowledge(data); })
      .catch(() => {})
      .finally(() => setKnowledgeLoading(false));

    fetch('/api/settings/inbox-retention')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRetention(data); })
      .catch(() => {})
      .finally(() => setRetentionLoading(false));

    fetch('/api/settings/otp-retention')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setOtpRetention(data); })
      .catch(() => {});

    fetch('/api/settings/reclassification-offers')
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setOffers(data); })
      .catch(() => {})
      .finally(() => setOffersLoading(false));

    fetch('/api/settings/reply-hints')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && typeof data.count === 'number') setReplyHintsCount(data.count); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!sweepStatus?.active) return;
    const id = setInterval(() => {
      fetch('/api/inbox/sweep/status')
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setSweepStatus(data); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [sweepStatus?.active]);

  async function updatePref(patch: Partial<UserPreferences>) {
    const updated = { ...prefs, ...patch };
    setPrefs(updated);
    await fetch('/api/preferences', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    }).catch(() => {});
  }

  async function disconnectGmail() {
    setDisconnecting(true);
    try {
      await fetch('/api/auth/gmail/disconnect', { method: 'POST' });
      setGmailStatus(null);
      setDisconnectConfirm(false);
    } catch {
      // best-effort
    } finally {
      setDisconnecting(false);
    }
  }

  async function disconnectMicrosoft() {
    setDisconnecting(true);
    try {
      await fetch('/api/auth/microsoft/disconnect', { method: 'POST' });
      setMsStatus(null);
      setDisconnectConfirm(false);
    } catch {
      // best-effort
    } finally {
      setDisconnecting(false);
    }
  }

  const connectedProvider = gmailStatus?.connected ? 'gmail' : msStatus?.connected ? 'microsoft' : null;
  const connectedEmailCount = gmailStatus?.connected ? gmailStatus.emailCount : msStatus?.emailCount ?? 0;
  const connectedProviderLabel = connectedProvider === 'gmail' ? 'Gmail' : connectedProvider === 'microsoft' ? 'Outlook' : null;

  async function openBillingPortal() {
    setPortalLoading(true);
    try {
      const r = await fetch('/api/billing/portal', { method: 'POST' });
      const { url } = await r.json();
      window.location.href = url;
    } catch {
      setPortalLoading(false);
    }
  }

  async function startSweep() {
    setSweepConfirm(false);
    setSweepStarting(true);
    setSweepError(null);
    try {
      const res = await fetch('/api/inbox/sweep', { method: 'POST' });
      if (res.status === 409) {
        setSweepError(t('settings.inboxTools.sweepErrorInProgress'));
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSweepError(data.error === 'reauth_required'
          ? t('settings.inboxTools.sweepErrorReauth')
          : t('settings.inboxTools.sweepErrorGeneric'));
        return;
      }
      setSweepStatus({ active: true, status: 'pending', totalSwept: 0 });
    } catch {
      setSweepError(t('settings.inboxTools.sweepErrorGeneric'));
    } finally {
      setSweepStarting(false);
    }
  }

  function sweepStatusLabel(status: string): string {
    switch (status) {
      case 'pending':    return t('settings.inboxTools.sweepPending');
      case 'processing': return t('settings.inboxTools.sweepProcessing');
      case 'cleanup':    return t('settings.inboxTools.sweepCleanup');
      case 'cancelling': return t('settings.inboxTools.sweepCancelling');
      case 'undoing':    return t('settings.inboxTools.sweepUndoing');
      default:           return status;
    }
  }

  const confirmSender = useCallback(async (senderEmail: string) => {
    await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/knowledge`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'user_confirmed' }),
    }).catch(() => {});
    setKnowledge(prev => prev.filter(e => e.senderEmail !== senderEmail));
  }, []);

  const changeSenderCategory = useCallback(async (senderEmail: string, newCategory: string) => {
    await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/category`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: newCategory }),
    }).catch(() => {});
    await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/knowledge`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'user_confirmed' }),
    }).catch(() => {});
    setKnowledge(prev => prev.filter(e => e.senderEmail !== senderEmail));
  }, []);

  function startEditRetention(category: string, entry: RetentionEntry) {
    setRetentionEditing(category);
    setRetentionEditValue(
      entry.hasOverride
        ? (entry.userOverride === null ? '' : String(entry.userOverride))
        : (entry.systemDefault === null ? '' : String(entry.systemDefault))
    );
  }

  async function saveRetention(category: string) {
    setRetentionSaving(true);
    const days: number | null = retentionEditValue === '' ? null : parseInt(retentionEditValue, 10);
    await fetch(`/api/settings/inbox-retention/${category}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days }),
    }).catch(() => {});
    setRetention(prev => prev ? {
      ...prev,
      [category]: {
        ...prev[category],
        userOverride: days,
        hasOverride: true,
        effective: days,
      },
    } : prev);
    setRetentionEditing(null);
    setRetentionSaving(false);
  }

  async function resetRetention(category: string) {
    await fetch(`/api/settings/inbox-retention/${category}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days: 'reset' }),
    }).catch(() => {});
    setRetention(prev => {
      if (!prev) return prev;
      const systemDefault = prev[category]?.systemDefault ?? null;
      return {
        ...prev,
        [category]: {
          ...prev[category],
          userOverride: null,
          hasOverride: false,
          effective: systemDefault,
        },
      };
    });
    setRetentionEditing(null);
  }

  function formatDays(days: number | null) {
    return days === null ? t('settings.inboxRetention.keep') : t('settings.inboxRetention.custom', { days });
  }

  function formatHours(hours: number) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  async function saveOtpRetention() {
    setOtpRetentionSaving(true);
    const hours = parseInt(otpRetentionEditValue, 10);
    await fetch('/api/settings/otp-retention', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hours }),
    }).catch(() => {});
    setOtpRetention(prev => prev ? { ...prev, userOverride: hours, hasOverride: true, effective: hours } : prev);
    setOtpRetentionEditing(false);
    setOtpRetentionSaving(false);
  }

  async function resetOtpRetention() {
    await fetch('/api/settings/otp-retention', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hours: 'reset' }),
    }).catch(() => {});
    setOtpRetention(prev => prev ? { ...prev, userOverride: null, hasOverride: false, effective: prev.systemDefault } : prev);
    setOtpRetentionEditing(false);
  }

  async function acceptOffer(offer: ReclassificationOffer) {
    setOfferActioning(offer.id);
    try {
      await fetch(`/api/settings/reclassification-offers/${offer.id}/accept`, { method: 'POST' });
      setOffers(prev => prev.filter(o => o.id !== offer.id));
    } catch {
      // leave offer in list on error
    } finally {
      setOfferActioning(null);
    }
  }

  async function dismissOffer(offer: ReclassificationOffer) {
    setOfferActioning(offer.id);
    try {
      await fetch(`/api/settings/reclassification-offers/${offer.id}/dismiss`, { method: 'POST' });
      setOffers(prev => prev.filter(o => o.id !== offer.id));
    } catch {
      // leave offer in list on error
    } finally {
      setOfferActioning(null);
    }
  }

  const planLabel = billing.plan ? t(`settings.billing.plans.${billing.plan}`) : null;
  const statusLabel = t(`settings.billing.statuses.${billing.status}`, { defaultValue: billing.status });
  const canManageBilling = billing.status !== 'none' && billing.paymentProvider === 'stripe';

  return (
    <AppShellLayout>
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
        <p>{t('settings.subtitle')}</p>
      </div>

      {/* Account */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.account.title')}</h2>
        <div className="settings-card">
          <div className="settings-row">
            <span className="settings-label">{t('settings.account.signedInAs')}</span>
            <span className="settings-value">{user?.userDetails ?? '—'}</span>
          </div>
          <div className="settings-row settings-row--action">
            <span className="settings-label">{t('settings.account.signOut')}</span>
            <a href="/.auth/logout" className="btn-secondary">{t('settings.account.signOut')}</a>
          </div>
        </div>
      </div>

      {/* Connected accounts */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.connectedAccounts.title')}</h2>
        <div className="settings-card">
          <div className="settings-row settings-row--action">
            <div>
              <span className="settings-label">{connectedProviderLabel ?? 'Email account'}</span>
              {!gmailLoading && (
                <span className={`settings-status-badge ${connectedProvider ? 'settings-status-badge--ok' : 'settings-status-badge--warn'}`}>
                  {connectedProvider
                    ? t('settings.connectedAccounts.connectedCount', { count: connectedEmailCount.toLocaleString() })
                    : t('settings.inbox.notConnected')}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/app/connect" className="btn-secondary">
                {connectedProvider ? t('settings.inbox.reconnect') : t('connect.connectButton', { label: 'an account' })}
              </Link>
              {connectedProvider && !disconnectConfirm && (
                <button className="btn-secondary" style={{ color: 'var(--danger)' }}
                  onClick={() => setDisconnectConfirm(true)}>
                  {t('settings.connectedAccounts.disconnect')}
                </button>
              )}
              {disconnectConfirm && (
                <>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                    {t('settings.connectedAccounts.disconnectConfirm')}
                  </span>
                  <button className="btn-danger"
                    onClick={connectedProvider === 'microsoft' ? disconnectMicrosoft : disconnectGmail}
                    disabled={disconnecting}>
                    {disconnecting ? t('common.loading') : t('settings.connectedAccounts.disconnectConfirmButton')}
                  </button>
                  <button className="btn-secondary" onClick={() => setDisconnectConfirm(false)}>
                    {t('common.cancel')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.subscription.title')}</h2>
        <div className="settings-card">
          {billingLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '120px' }} />
            </div>
          ) : (
            <>
              <div className="settings-row">
                <span className="settings-label">{t('settings.billing.plan')}</span>
                <span className="settings-value">
                  {planLabel ? `${planLabel} — ${statusLabel}` : statusLabel}
                </span>
              </div>
              {billing.currentPeriodEnd && billing.status === 'active' && (
                <div className="settings-row">
                  <span className="settings-label">{t('settings.subscription.renews')}</span>
                  <span className="settings-value">
                    {new Date(billing.currentPeriodEnd).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </span>
                </div>
              )}
              {billing.trialEndsAt && billing.status === 'trialing' && (
                <div className="settings-row">
                  <span className="settings-label">{t('settings.subscription.trialEnds')}</span>
                  <span className="settings-value">
                    {new Date(billing.trialEndsAt).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </span>
                </div>
              )}
              <div className="settings-row settings-row--action">
                <span className="settings-label">
                  {billing.status === 'none' ? t('settings.subscription.startFreeTrial') : t('settings.subscription.changePlan')}
                </span>
                {canManageBilling ? (
                  <button
                    className="btn-secondary"
                    onClick={openBillingPortal}
                    disabled={portalLoading}
                  >
                    {portalLoading ? t('common.loading') : t('settings.subscription.manageBilling')}
                  </button>
                ) : (
                  <Link to="/pricing" className="btn-secondary">
                    {billing.status === 'none' ? t('billing.seePlans') : t('settings.subscription.upgradePlan')}
                  </Link>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.preferences.title')}</h2>
        <div className="settings-card">
          {prefsLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '160px' }} />
            </div>
          ) : (
            <>
              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">{t('settings.preferences.markReadOnArchive')}</div>
                  <div className="settings-toggle-description">{t('settings.preferences.markReadOnArchiveDesc')}</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={prefs.markAsReadOnArchive}
                    onChange={e => updatePref({ markAsReadOnArchive: e.target.checked })}
                  />
                  <span className="toggle-track" />
                </label>
              </div>
              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">{t('settings.preferences.markReadOnTrash')}</div>
                  <div className="settings-toggle-description">{t('settings.preferences.markReadOnTrashDesc')}</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={prefs.markAsReadOnTrash}
                    onChange={e => updatePref({ markAsReadOnTrash: e.target.checked })}
                  />
                  <span className="toggle-track" />
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Email preferences */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.emailPrefs.title')}</h2>
        <div className="settings-card">
          {prefsLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '160px' }} />
            </div>
          ) : (
            <>
              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">{t('settings.emailPrefs.weeklyDigest')}</div>
                  <div className="settings-toggle-description">{t('settings.emailPrefs.weeklyDigestDesc')}</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={prefs.digestEmails}
                    onChange={e => updatePref({ digestEmails: e.target.checked })}
                  />
                  <span className="toggle-track" />
                </label>
              </div>
              <div className="settings-toggle-row">
                <div>
                  <div className="settings-toggle-label">{t('settings.emailPrefs.monthlySummary')}</div>
                  <div className="settings-toggle-description">{t('settings.emailPrefs.monthlySummaryDesc')}</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={prefs.summaryEmails}
                    onChange={e => updatePref({ summaryEmails: e.target.checked })}
                  />
                  <span className="toggle-track" />
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Reply drafts */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.draftReplies.title')}</h2>
        <div className="settings-card">
          {prefsLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '160px' }} />
            </div>
          ) : (
            <div className="settings-toggle-row">
              <div>
                <div className="settings-toggle-label">{t('settings.draftReplies.autoDraftLabel')}</div>
                <div className="settings-toggle-description">{t('settings.draftReplies.autoDraftDesc')}</div>
                <div className="settings-toggle-description" style={{ marginTop: '4px', fontSize: '0.75rem', opacity: 0.7 }}>
                  {t('settings.draftReplies.autoDraftNote')}
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={prefs.autoDraftReplies}
                  onChange={e => updatePref({ autoDraftReplies: e.target.checked })}
                />
                <span className="toggle-track" />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Reply tracking */}
      <div className="settings-section">
        <h2 className="settings-section-title">Reply Tracking</h2>
        <div className="settings-card">
          <div className="settings-row settings-row--action">
            <div>
              <div className="settings-toggle-label">Confirmed reply senders</div>
              <div className="settings-toggle-description">
                When you apply the <strong>Needs Reply (manual)</strong> Gmail label to a missed email,
                the system remembers that sender. Future emails from them will be more likely to receive the To Respond label.
              </div>
              {replyHintsCount !== null && (
                <div className="settings-toggle-description" style={{ marginTop: 4 }}>
                  {replyHintsCount === 0
                    ? 'No senders confirmed yet.'
                    : `${replyHintsCount} sender${replyHintsCount === 1 ? '' : 's'} confirmed.`}
                </div>
              )}
            </div>
            {replyHintsCount !== null && replyHintsCount > 0 && (
              <button
                className="btn-secondary"
                disabled={replyHintsClearLoading}
                style={{ flexShrink: 0 }}
                onClick={async () => {
                  setReplyHintsClearLoading(true);
                  try {
                    await fetch('/api/settings/reply-hints', { method: 'DELETE' });
                    setReplyHintsCount(0);
                  } catch { /* silent */ }
                  finally { setReplyHintsClearLoading(false); }
                }}
              >
                {replyHintsClearLoading ? 'Clearing…' : 'Clear all'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inbox triage */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.inboxTriage.title')}</h2>
        <div className="settings-card">
          {prefsLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '160px' }} />
            </div>
          ) : (
            <>
            <div className="settings-toggle-row">
              <div>
                <div className="settings-toggle-label">{t('settings.inboxTriage.respondRetentionLabel')}</div>
                <div className="settings-toggle-description">{t('settings.inboxTriage.respondRetentionDesc')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <input
                  type="number"
                  min={1}
                  max={30}
                  className="search-input"
                  style={{ width: 64, textAlign: 'center' }}
                  value={prefs.toRespondRetentionDays}
                  onChange={e => {
                    const val = Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 7));
                    updatePref({ toRespondRetentionDays: val });
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t('settings.preferences.days')}</span>
              </div>
            </div>
            <div className="settings-toggle-row">
              <div>
                <div className="settings-toggle-label">{t('settings.inboxTriage.respondMaxAgeLabel')}</div>
                <div className="settings-toggle-description">{t('settings.inboxTriage.respondMaxAgeDesc')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <input
                  type="number"
                  min={1}
                  className="search-input"
                  style={{ width: 64, textAlign: 'center' }}
                  placeholder="30"
                  value={prefs.toRespondMaxAgeDays ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    updatePref({ toRespondMaxAgeDays: raw === '' ? null : Math.max(1, parseInt(raw, 10) || 30) });
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t('settings.preferences.days')}</span>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Inbox retention */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.inboxRetention.title')}</h2>
        <div className="settings-card">
          {retentionLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '200px' }} />
            </div>
          ) : !retention ? (
            <div className="settings-row">
              <span className="settings-value settings-value--muted">{t('settings.inboxRetention.loadError')}</span>
            </div>
          ) : (
            <div style={{ padding: '14px 20px' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                {t('settings.inboxRetention.description')}
              </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.inboxRetention.category')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.inboxRetention.threshold')}</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {Object.entries(RETENTION_CATEGORY_KEYS).map(([category, labelKey]) => {
                  const entry = retention[category];
                  if (!entry) return null;
                  const isEditing = retentionEditing === category;
                  const displayValue = entry.hasOverride ? entry.userOverride : entry.systemDefault;
                  return (
                    <tr key={category} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 8px' }}>{t(labelKey)}</td>
                      <td style={{ padding: '8px 8px' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="number"
                              min={1}
                              max={3650}
                              className="search-input"
                              style={{ width: 64, textAlign: 'center' }}
                              placeholder={t('settings.inboxRetention.keep')}
                              value={retentionEditValue}
                              onChange={e => setRetentionEditValue(e.target.value)}
                              autoFocus
                            />
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('settings.inboxRetention.keepDaysHint')}</span>
                          </div>
                        ) : (
                          <span style={entry.hasOverride ? undefined : { color: 'var(--text-secondary)' }}>
                            {formatDays(displayValue)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {isEditing ? (
                          <>
                            <button
                              className="btn-secondary"
                              style={{ marginRight: 6 }}
                              onClick={() => saveRetention(category)}
                              disabled={retentionSaving}
                            >
                              {retentionSaving ? t('settings.inboxRetention.saving') : t('settings.inboxRetention.save')}
                            </button>
                            {entry.hasOverride && (
                              <button
                                className="btn-ghost"
                                style={{ marginRight: 6 }}
                                onClick={() => resetRetention(category)}
                              >
                                {t('settings.inboxRetention.reset')}
                              </button>
                            )}
                            <button
                              className="btn-ghost"
                              onClick={() => setRetentionEditing(null)}
                            >
                              {t('settings.inboxRetention.cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn-ghost"
                            onClick={() => startEditRetention(category, entry)}
                          >
                            {t('settings.inboxRetention.edit')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {otpRetention && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 8px' }}>OTP &amp; Login Links</td>
                    <td style={{ padding: '8px 8px' }}>
                      {otpRetentionEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="number"
                            min={1}
                            max={168}
                            className="search-input"
                            style={{ width: 64, textAlign: 'center' }}
                            placeholder="24"
                            value={otpRetentionEditValue}
                            onChange={e => setOtpRetentionEditValue(e.target.value)}
                            autoFocus
                          />
                          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>hours then delete</span>
                        </div>
                      ) : (
                        <span style={otpRetention.hasOverride ? undefined : { color: 'var(--text-secondary)' }}>
                          {formatHours(otpRetention.effective)} then delete
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {otpRetentionEditing ? (
                        <>
                          <button
                            className="btn-secondary"
                            style={{ marginRight: 6 }}
                            onClick={saveOtpRetention}
                            disabled={otpRetentionSaving}
                          >
                            {otpRetentionSaving ? t('settings.inboxRetention.saving') : t('settings.inboxRetention.save')}
                          </button>
                          {otpRetention.hasOverride && (
                            <button
                              className="btn-ghost"
                              style={{ marginRight: 6 }}
                              onClick={resetOtpRetention}
                            >
                              {t('settings.inboxRetention.reset')}
                            </button>
                          )}
                          <button
                            className="btn-ghost"
                            onClick={() => setOtpRetentionEditing(false)}
                          >
                            {t('settings.inboxRetention.cancel')}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-ghost"
                          onClick={() => { setOtpRetentionEditing(true); setOtpRetentionEditValue(String(otpRetention.effective)); }}
                        >
                          {t('settings.inboxRetention.edit')}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Corrected classifications (re-label offers) */}
      {!offersLoading && offers.length > 0 && (
        <div className="settings-section">
          <h2 className="settings-section-title">{t('settings.senderKnowledge.title')}</h2>
          <div className="settings-card">
            <div style={{ padding: '14px 20px' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                {t('settings.senderKnowledge.description')}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.senderKnowledge.sender')}</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.senderKnowledge.changeCol')}</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }} />
                  </tr>
                </thead>
                <tbody>
                  {offers.map(offer => (
                    <tr key={offer.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 8px', wordBreak: 'break-all' }}>{offer.senderEmail}</td>
                      <td style={{ padding: '8px 8px' }}>
                        <span className={`category-pill category-pill--${offer.oldCategory}`}>{offer.oldCategory}</span>
                        {' → '}
                        <span className={`category-pill category-pill--${offer.newCategory}`}>{offer.newCategory}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 6 }}>
                          ({offer.emailCount} {t('subscriptions.emails')})
                        </span>
                      </td>
                      <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          className="btn-secondary"
                          style={{ marginRight: 6 }}
                          disabled={offerActioning === offer.id}
                          onClick={() => acceptOffer(offer)}
                        >
                          {t('settings.senderKnowledge.relabel')}
                        </button>
                        <button
                          className="btn-ghost"
                          disabled={offerActioning === offer.id}
                          onClick={() => dismissOffer(offer)}
                        >
                          {t('settings.senderKnowledge.dismiss')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Sender classifications */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.senderClassifications.title')}</h2>
        <div className="settings-card">
          {knowledgeLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '200px' }} />
            </div>
          ) : knowledge.length === 0 ? (
            <div className="settings-row">
              <span className="settings-value settings-value--muted">
                {t('settings.senderClassifications.empty')}
              </span>
            </div>
          ) : (
            <div style={{ padding: '14px 20px' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                {t('settings.senderClassifications.desc')}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.senderKnowledge.sender')}</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('settings.senderClassifications.suggestedCat')}</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px' }} />
                  </tr>
                </thead>
                <tbody>
                  {knowledge.map(entry => (
                    <tr key={entry.senderEmail} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 8px', wordBreak: 'break-all' }}>{entry.senderEmail}</td>
                      <td style={{ padding: '8px 8px' }}>
                        <span className={`category-pill category-pill--${entry.category}`}>
                          {entry.category}
                        </span>
                        {entry.confidence !== null && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 6 }}>
                            {Math.round(entry.confidence * 100)}%
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          className="btn-secondary"
                          style={{ marginRight: 6 }}
                          onClick={() => confirmSender(entry.senderEmail)}
                        >
                          {t('settings.senderClassifications.confirm')}
                        </button>
                        <select
                          className="search-input"
                          style={{ fontSize: '0.8rem' }}
                          defaultValue=""
                          onChange={e => {
                            if (e.target.value) changeSenderCategory(entry.senderEmail, e.target.value);
                          }}
                        >
                          <option value="" disabled>{t('settings.senderClassifications.change')}</option>
                          {['personal', 'marketing', 'subscription', 'transactional', 'notification'].map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Inbox tools */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.inboxTools.title')}</h2>
        <div className="settings-card">
          {sweepLoading ? (
            <div className="settings-row">
              <div className="skeleton skeleton-text" style={{ width: '160px' }} />
            </div>
          ) : (
            <div className="settings-row settings-row--action">
              <div>
                <div className="settings-toggle-label">{t('settings.inboxTools.sweepLabel')}</div>
                <div className="settings-toggle-description">{t('settings.inboxTools.sweepDesc')}</div>
                {sweepStatus?.active && (
                  <span className="settings-status-badge settings-status-badge--ok" style={{ marginTop: '4px', display: 'inline-block' }}>
                    {sweepStatusLabel(sweepStatus.status ?? '')}
                    {(sweepStatus.totalSwept ?? 0) > 0 && ` · ${t('settings.inboxTools.swept', { count: sweepStatus.totalSwept!.toLocaleString() })}`}
                  </span>
                )}
                {!sweepStatus?.active && (gmailStatus?.isIngesting || msStatus?.isIngesting) && (
                  <span className="settings-status-badge settings-status-badge--warn" style={{ marginTop: '4px', display: 'inline-block' }}>
                    {t('settings.inboxTools.sweepInProgress')}
                  </span>
                )}
                {sweepError && (
                  <div className="inline-banner inline-banner--error" style={{ marginTop: '8px' }}>
                    {sweepError}
                  </div>
                )}
                {sweepConfirm && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      {t('settings.inboxTools.sweepConfirmText')}
                    </span>
                    <button className="btn-secondary" onClick={startSweep} disabled={sweepStarting}>
                      {sweepStarting ? t('settings.inboxTools.sweepPending') : t('settings.inboxTools.sweepYes')}
                    </button>
                    <button className="btn-secondary" onClick={() => { setSweepConfirm(false); setSweepError(null); }}>
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
              </div>
              {!sweepConfirm && (
                <button
                  className="btn-secondary"
                  disabled={sweepStatus?.active || sweepStarting || !connectedProvider || gmailStatus?.isIngesting || msStatus?.isIngesting}
                  onClick={() => { setSweepConfirm(true); setSweepError(null); }}
                  title={!connectedProvider ? t('connect.connectButton', { label: 'an account' }) : (gmailStatus?.isIngesting || msStatus?.isIngesting) ? t('settings.inboxTools.sweepInProgress') : undefined}
                >
                  {sweepStatus?.active ? sweepStatusLabel(sweepStatus.status ?? '') : t('settings.inboxTools.sweepInbox')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data */}
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.yourData.title')}</h2>
        <div className="settings-card">
          <div className="settings-row">
            <div>
              <div className="settings-toggle-label">{t('settings.yourData.label')}</div>
              <div className="settings-toggle-description">
                <Trans
                  i18nKey="settings.yourData.desc"
                  components={{ link: <a href="mailto:privacy@theemailhelper.com" aria-label="privacy@theemailhelper.com" /> }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="settings-section">
        <h2 className="settings-section-title" style={{ color: 'var(--color-error, #e53e3e)' }}>{t('settings.dangerZone.title')}</h2>
        <div className="settings-card" style={{ borderColor: 'var(--color-error, #e53e3e)' }}>
          <div className="settings-row settings-row--action">
            <div>
              <div className="settings-toggle-label">{t('settings.dangerZone.deleteAccount')}</div>
              <div className="settings-toggle-description">{t('settings.dangerZone.deleteDescription')}</div>
            </div>
            <button
              className="btn-secondary"
              style={{ color: 'var(--color-error, #e53e3e)', borderColor: 'var(--color-error, #e53e3e)', flexShrink: 0 }}
              onClick={() => { setDeleteModalOpen(true); setDeleteConfirmEmail(''); setDeleteError(null); }}
            >
              {t('settings.dangerZone.deleteButton')}
            </button>
          </div>
        </div>
      </div>

      {/* Delete account confirmation modal */}
      {deleteModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: '12px', padding: '32px',
            maxWidth: '440px', width: '100%', margin: '0 16px',
          }}>
            <h3 style={{ marginBottom: '12px', color: 'var(--color-error, #e53e3e)' }}>
              {t('settings.deleteModal.title')}
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.5' }}>
              {t('settings.deleteModal.body')}
            </p>
            <p style={{ fontSize: '0.9rem', marginBottom: '8px' }}>
              {t('settings.deleteModal.confirmLabel')}
            </p>
            <input
              type="email"
              className="search-input"
              style={{ width: '100%', marginBottom: '16px' }}
              placeholder={user?.userDetails ?? 'your@email.com'}
              value={deleteConfirmEmail}
              onChange={e => setDeleteConfirmEmail(e.target.value)}
              autoFocus
            />
            {deleteError && (
              <div className="inline-banner inline-banner--error" style={{ marginBottom: '16px' }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleteSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-secondary"
                style={{
                  color: 'var(--color-error, #e53e3e)',
                  borderColor: 'var(--color-error, #e53e3e)',
                  opacity: deleteConfirmEmail !== user?.userDetails ? 0.5 : 1,
                }}
                disabled={deleteSubmitting || deleteConfirmEmail !== user?.userDetails}
                onClick={async () => {
                  setDeleteSubmitting(true);
                  setDeleteError(null);
                  try {
                    const res = await fetch('/api/user/delete', { method: 'POST' });
                    if (!res.ok) {
                      const data = await res.json().catch(() => ({}));
                      setDeleteError(data.error ?? t('settings.deleteModal.errorGeneric'));
                      setDeleteSubmitting(false);
                      return;
                    }
                    setTimeout(() => { window.location.href = '/.auth/logout'; }, 1500);
                  } catch {
                    setDeleteError(t('settings.deleteModal.errorGeneric'));
                    setDeleteSubmitting(false);
                  }
                }}
              >
                {deleteSubmitting ? t('settings.deleteModal.submitting') : t('settings.deleteModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShellLayout>
  );
}
